import { toast } from "react-toastify";

// Use localStorage events for cross-tab communication (more reliable than BroadcastChannel)
const STORAGE_KEY_SHOW = "command_toast_show";
const STORAGE_KEY_DISMISS = "command_toast_dismiss";

type ToastData = {
  fromUser: { id: number; name: string; username: string };
  label: string;
  copyValue: string;
  navigateTo: string | null;
};

let showToastHandler: ((toastId: string, data: ToastData) => void) | null = null;

// Initialize cross-tab sync
export function initToastSync(onShowToast: (toastId: string, data: ToastData) => void) {
  showToastHandler = onShowToast;

  // Listen for storage events (fired when another tab changes localStorage)
  const handleStorageEvent = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY_SHOW && e.newValue) {
      try {
        const { toastId, data, timestamp } = JSON.parse(e.newValue);
        // Only process if this is a new event (within last 5 seconds)
        if (Date.now() - timestamp < 5000 && showToastHandler) {
          showToastHandler(toastId, data);
        }
      } catch (err) {
        console.error("[ToastSync] Failed to parse show event:", err);
      }
    }
    
    if (e.key === STORAGE_KEY_DISMISS && e.newValue) {
      try {
        const { toastId, timestamp } = JSON.parse(e.newValue);
        if (Date.now() - timestamp < 5000) {
          toast.dismiss(toastId);
        }
      } catch (err) {
        console.error("[ToastSync] Failed to parse dismiss event:", err);
      }
    }
  };

  window.addEventListener("storage", handleStorageEvent);
  console.log("[ToastSync] Initialized cross-tab sync via localStorage");

  // Return cleanup function
  return () => {
    window.removeEventListener("storage", handleStorageEvent);
  };
}

// Broadcast to show toast on other tabs
export function broadcastShowToast(toastId: string, data: ToastData) {
  try {
    localStorage.setItem(STORAGE_KEY_SHOW, JSON.stringify({ 
      toastId, 
      data, 
      timestamp: Date.now() 
    }));
    // Clear immediately (the event is already fired)
    localStorage.removeItem(STORAGE_KEY_SHOW);
  } catch (err) {
    console.error("[ToastSync] Failed to broadcast show:", err);
  }
}

// Track active command toast IDs
const activeToasts = new Set<string>();

export function addActiveToast(toastId: string) {
  activeToasts.add(toastId);
}

export function removeActiveToast(toastId: string) {
  activeToasts.delete(toastId);
}

// Broadcast to dismiss toast on ALL tabs (including this one)
export function broadcastDismissToast(toastId: string) {
  console.log("[ToastSync] Dismissing toast:", toastId, "Active toasts:", Array.from(activeToasts));
  
  // Remove from tracking
  removeActiveToast(toastId);
  
  // Dismiss on this tab immediately - try multiple methods
  try {
    // Method 1: Dismiss by ID
    const result = toast.dismiss(toastId);
    console.log("[ToastSync] toast.dismiss result:", result);
    
    // Method 2: Small delay and try again (sometimes needed)
    setTimeout(() => {
      toast.dismiss(toastId);
    }, 100);
  } catch (err) {
    console.error("[ToastSync] Error dismissing:", err);
  }
  
  // Broadcast to other tabs
  try {
    localStorage.setItem(STORAGE_KEY_DISMISS, JSON.stringify({ 
      toastId, 
      timestamp: Date.now() 
    }));
    // Clear after a small delay
    setTimeout(() => localStorage.removeItem(STORAGE_KEY_DISMISS), 100);
  } catch (err) {
    console.error("[ToastSync] Failed to broadcast dismiss:", err);
  }
}

// Broadcast accept (same as dismiss, just for clarity)
export function broadcastAcceptToast(toastId: string, _navigateTo: string | null) {
  broadcastDismissToast(toastId);
}

// Cleanup
export function closeToastSync() {
  showToastHandler = null;
  console.log("[ToastSync] Closed");
}
